/** @jsxImportSource @emotion/react */
import React, { useImperativeHandle, useMemo, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import { CellType } from '@grapecity/wijmo.grid';
import { CellMaker } from '@grapecity/wijmo.grid.cellmaker';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import CustomGrid from 'components/grids/CustomGrid';
import { getExcelFileName } from 'utils/ExcelUtil';
import { useMessageBar } from 'hooks/useMessageBar';
import { useLoading } from 'components/process/Loading';
import { SetupSchedule, SetupScheduleCondition } from 'models/setup/SetupSchedule';
import { findExcpByElmPrjId, findSchedule } from 'apis/setup/SetupSchedule';
import useSessionStore from 'stores/useSessionStore';
import { ApproveRequestPageType } from '../../approves/ApproveRequestDetailCase';

/**
 * @desc 설비셋업관리 > 프로젝트 셋업관리 > 프로젝트 일정 등록/조회 > [일정등록/조회] 탭 내용
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.10.24   jshwang12       신규 생성
 * 2025.11.05   jshwang12       템플릿 다운로드 버튼 삭제 (결재요청 화면에서만 다운로드 가능)
 * 2025.12.08   jshwang12       일정 등록 버튼 삭제 (해당 화면은 일정등록, 업데이트 요청 시 프로젝트명 클릭하여 이동 가능)
 */

interface Props {
  onScheduleRegist: (
    aprReqTpCd: string,
    elmPrjId: string,
    excpId: string,
    aprReqId?: string
  ) => void;
  onReadRequest: (aprReqTpCd: string, aprReqId: string) => void;
}

const ScheduleRegistContent = ({ onScheduleRegist, onReadRequest, ...props }: Props, ref) => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const { ensolYn } = useSessionStore();
  const { openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const [condition, setCondition] = useState<SetupScheduleCondition>();
  const [totalCount, setTotalCount] = useState<number>(0);
  const [rowData, setRowData] = useState<SetupSchedule[]>([]);
  const [prjExcpData, setPrjExcpData] = useState<SetupSchedule>();

  useImperativeHandle(ref, () => ({
    search: (params) => {
      setCondition(params);
      handleSearch(params);
    },
    validate: () => {
      return validate();
    },
  }));

  useEffect(() => {
    if (condition?.elmPrjId && condition?.excpId) {
      findExcpByElmPrjId(condition.elmPrjId, condition.excpId).then((result) => {
        setPrjExcpData((result || []).length > 0 ? result[0] : undefined);
      });
    } else {
      setPrjExcpData(undefined);
    }
  }, [condition?.elmPrjId, condition?.excpId]);

  const handleSearch = async (params) => {
    const response = await findSchedule(params);
    setTotalCount((response || []).length);
    // 협력사
    const findItemParent = (items: any[], excpId, parent?: any) => {
      if (parent && parent.excpId === excpId) {
        return parent;
      }

      for (const item of items) {
        if (item.excpId === excpId) {
          return findItemParent(items, excpId, item);
        }
        if (Object.prototype.hasOwnProperty.call(item, 'projectes')) {
          return findItemParent(item?.projectes, excpId, item);
        }
      }

      return parent;
    };

    const rowData = (response || []).reduce((acc, cur) => {
      // 일치하는 협력업체가 없는 경우 최상위 객체 추가
      if (!acc.map((o) => o.excpId).includes(cur.excpId)) {
        return [...acc, cur];
      }
      const parent = findItemParent(acc, cur.excpId);
      if (parent) {
        const key = 'projects';
        if (!parent[key]) {
          parent[key] = [];
        }
        parent[key].push(cur);
      }
      return acc;
    }, [] as SetupSchedule[]);

    setRowData(rowData);
  };

  const validate = () => {
    if (!condition?.elmPrjId) {
      openMessageBar({
        type: 'warning',
        content: t(
          'setup.label.대상 프로젝트가 조회 되지 않았습니다.',
          '프로젝트가 조회 되지 않았습니다.'
        ),
      });
      return false;
    }
    if (!condition?.excpId) {
      openMessageBar({
        type: 'warning',
        content: t('setup.label.협력업체를 선택해 주세요.', '협력업체를 선택해 주세요.'),
      });
      return false;
    }
    if (!prjExcpData) {
      openMessageBar({
        type: 'warning',
        content: t('setup.label.협력업체 정보가 없습니다.', '협력업체 정보가 없습니다.'),
      });
      return false;
    }
    return true;
  };

  const layoutDefinition = useMemo(
    () => [
      {
        binding: 'bildPlntCdNm',
        header: String(t('setup.label.플랜트', '플랜트')),
        minWidth: 80,
        width: 165,
        align: 'left',
      },
      {
        binding: 'elmPrjNm',
        header: String(t('setup.label.프로젝트명', '프로젝트명')),
        minWidth: 150,
        width: '*',
        align: 'left',
        cellTemplate: CellMaker.makeLink({
          text: '<span>${item.elmPrjNm}</span>',
          click: (e, ctx) => {
            const item = ctx.item;
            // 임시저장 상태인 경우
            if ('TMP' === item.aprReqProgStatCd) {
              // 작성자: 임시저장된 결재화면 오픈
              // 작성자 외: 임시저장 상태는 작성자만 등록 진행할 수 있습니다.
              onScheduleRegist(item?.aprReqTpCd, item?.elmPrjId, item?.excpId, item?.aprReqId);
            }
            // 업체 일정등록 대기중 상태인 경우
            else if ('WAIT' === item.wkProgStatCd) {
              // 협력업체 내 권한있는 경우: 신규 등록 요청화면 오픈
              // 권한 없는 경우: 권한이 없습니다. 대상 협력업체 만이 등록 가능합니다.
              onScheduleRegist(item?.aprReqTpCd, item?.elmPrjId, item?.excpId, item?.aprReqId);
            }
            // 업체 일정 업데이트 요청 상태인 경우
            else if ('UPD_REQ' === item.wkProgStatCd) {
              onScheduleRegist(item?.aprReqTpCd, item?.elmPrjId, item?.excpId, item?.aprReqId);
            }
            // 그외 상태인 경우 결재요청서 상세 노출
            else if (item.aprReqId) {
              onReadRequest(item.aprReqTpCd, item.aprReqId);
            }
          },
        }),
      },
      {
        binding: 'excpNm',
        header: String(t('setup.label.협력업체명', '협력업체명')),
        minWidth: 120,
        width: 120,
        align: 'center',
      },
      {
        binding: 'aprTplNm',
        header: String(t('setup.label.요청 유형', '요청 유형')),
        minWidth: 100,
        width: 100,
        align: 'center',
      },
      {
        binding: 'aprReqProgStatCdNm',
        header: String(t('setup.label.상태', '상태')),
        minWidth: 100,
        width: 120,
        align: 'center',
        cssClass: 'WijmoTag',
        cellTemplate: (params) => {
          // 결재요청진행상태 || 협력업체일정등록상태
          const statCd = params.item.aprReqProgStatCd || params.item.wkProgStatCd;
          const statCdNm = params.value || params.item.wkProgStatCdNm;
          let color = 'grey';
          // APV: 승인, REJ: 반려, REQ: 대기, TMP: 임시저장
          // WAIT: 대기중, SAVE: 임시저장, UPD_REQ: 업데이트요청, REG_REQ: 등록요청, REG_CPLT: 등록완료, CHG_REQ: 변경요청, RJT: 반려
          if (['WAIT', 'UPD_REQ', 'TMP', 'SAVE'].includes(statCd)) {
            color = 'yellow';
          } else if (['REG_CPLT', 'APV'].includes(statCd)) {
            color = 'green';
          } else if (['CHG_REQ', 'REG_REQ', 'REQ'].includes(statCd)) {
            color = 'blue';
          } else if (['REJ', 'RJT'].includes(statCd)) {
            color = 'red';
          }
          return `<span class="${color}">${statCdNm}</span>`;
        },
      },
      {
        binding: 'dataInsUserNm',
        header: String(t('setup.label.작성자', '작성자')),
        minWidth: 100,
        width: 100,
        align: 'center',
      },
      {
        binding: 'dataInsDtm',
        header: String(t('setup.label.작성일', '작성일')),
        minWidth: 100,
        width: 100,
        align: 'center',
      },
      {
        binding: 'aprAprvUserNm',
        header: String(t('setup.label.결재자', '결재자')),
        minWidth: 100,
        width: 100,
        align: 'center',
      },
      {
        binding: 'aprAprvDtm',
        header: String(t('setup.label.결재완료일', '결재완료일')),
        minWidth: 100,
        width: 100,
        align: 'center',
      },
    ],
    []
  );

  const onInitialized = (grid) => {
    gridRef.current = grid;

    grid.formatItem.addHandler((s, e) => {
      const col = e.panel.columns[e.col];
      const row = e.panel.rows[e.row];
      if (e.panel.cellType === CellType.Cell) {
        if ('bildPlntCdNm' === col.binding) {
          let padding = row.level * 20;
          if (!row.hasChildren) {
            // 레벨1이면서 자식이 없는 경우 폴딩버튼있는 행과 padding-left 값 맞추기 위해 보정값+7로 수정
            padding += row.level === 0 ? 27 : 20;
          }
          e.cell.style.paddingLeft = `${padding > 0 ? padding : 5}px`;
        }
      }
    });
  };

  const handleDownloadExcel = () => {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(gridRef.current, {
      includeColumnHeaders: true,
      includeRowHeaders: true,
    });
    book.sheets[0].name = t('setup.label.일정등록/조회', '일정등록/조회');
    book.saveAsync(getExcelFileName(t('setup.label.일정등록/조회', '일정등록/조회')));
  };

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.일정 목록', '일정 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(totalCount || 0).toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => handleSearch(condition)}
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>

      <CustomGrid
        id={'schdRegistGrid'}
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={480}
        isSelector={false}
        isFilter={true}
        autoCheck={true}
        isReadOnly={true}
        allowSorting={false}
        allowPinning={false}
        allowDragging={false}
        excludeFilter={['bildPlntCdNm', 'elmPrjNm']}
        childItemsPath={['projects']}
        initialized={onInitialized}
      />

      {/*
      // [25.12.08] 일정등록버튼 삭제 -> 해당 화면은 일정 등록, 업데이트 요청 시 프로젝트명 클릭하여 이동 가능
      {ensolYn === 'N' && (
        <GlobalBtnGroup>
          <Button
            css={IconButton.button}
            className="icoNone primaryBtn"
            onClick={() => {
              if (validate()) {
                onScheduleRegist(
                  ApproveRequestPageType.SETUP_SCHD_REG,
                  condition?.elmPrjId || '',
                  condition?.excpId || ''
                );
              }
            }}
          >
            {t('setup.label.일정등록', '일정등록')}
          </Button>
        </GlobalBtnGroup>
        )}
        */}
    </>
  );
};

export default React.forwardRef(ScheduleRegistContent);
